/**
 * Adapter for 'drawers'.
 *
 * @module app/components/EventDataProcessor
 */
import _ from 'lodash';
import moment from 'moment-timezone';
import SelectionViewAdapter from '../views/SelectionViewAdapter';

/**
 * Replaces the '|' char in the name
 *
 * @param  {Object} entity entity for window.WHich the name is to be processed
 *
 * @return {string}        processed name
 */
var handleName = function handleName(entity) {

    return entity.name.replace(/\|/g, '');
};

/**
 * Market processing auxiliar functions
 *
 * @type {Object}
 *
 * @private
 */
var markets = {

    /**
     * Creates a new instance of the market with processed name and selections
     *
     * @param {Object} market to be processed
     *
     * @returns {Object}
     */
    clone: function clone(market) {

        market.selections = _.uniq(market.selections);
        markets.fillSelections(market);

        market.order = market.order || 0;
        market.name = handleName(market);

        return _.extend({}, market, {
            startDateTime: moment.utc(market.startDateTime).format(),
            selections: api.processSelections(market.selections, market.id),
        });
    },

    /**
     * Checks if a market belong to any collection or not
     *
     * @param {Object} market
     *
     * @returns {Boolean}
     *
     * @private
     */
    unassignedMarket: function unassignedMarket(market) {

        if (window.WH.sportsbook.hasOwnProperty('collections') &&
            window.WH.sportsbook.collections.hasOwnProperty('selected') &&
            window.WH.sportsbook.collections.selected.hasOwnProperty('id') &&
            window.WH.sportsbook.collections.selected.id !== '9999') {
            return false;
        } else {

            var isUnassigned = true;

            /**
             * Check single collection for the market group IDs
             *
             * @param {Object} singleCollection
             *
             * @returns {void}
             *
             * @private
             */
            var checkSingleCollection = function (singleCollection) {

                if (markets.filterByCollection(market, singleCollection.markets)) {
                    isUnassigned = false;
                }
            };

            _.each(window.WH.sportsbook.fullMarketCollections, checkSingleCollection);

            return isUnassigned;
        }
    },

    /**
     * Checks if a given market is valid for the collection market groups provided
     *
     * @param  {Object} market                    market to be checked
     * @param  {Object} selectedCollectionMarkets Array containing the markets avaialble in the selected collec.
     *
     * @return {Object} Squiz market group {id: [ids], name: 'marketGroupName', position: Integer}
     *
     * @private
     */
    filterByCollection: function filterByCollection(market, selectedCollectionMarkets) {

        return _.find(selectedCollectionMarkets, function (colMarket) {

            return _.includes(colMarket.id, market.marketGroupId);

        });

    },

    /**
     * Replaces the selection ids on the market by it's full selection Object to be processed
     *
     * @param  {Object} market Market to be processed
     *
     * @return {void}
     *
     * @private
     */
    fillSelections: function fillSelections(market) {

        /**
         * Replaces the selection id by the selection object
         *
         * @param  {string} selection id
         *
         * @return {void}
         *
         * @private
         */
        var replaceSelectionId = function (selection, index) {

            market.selections[index] = window.WH.sportsbook.model.selections[selection];
        };

        _.each(market.selections, replaceSelectionId);
    },

    /**
     * Set the position of a market.
     *
     * @param {Object} collectionMarkets Selected collection markets.
     * @param {Object} market Market entity.
     *
     * @returns {void}
     *
     * @private
     */
    setPosition: function setPosition(collectionMarkets, market) {

        var model = window.WH.sportsbook.model;

        var collectionMarket = markets.filterByCollection(market, collectionMarkets);

        if (!!collectionMarket && collectionMarket.position !== undefined) {

            model.markets[market.id].position = collectionMarket.position;

        } else {

            model.markets[market.id].position = 9999;
        }
    },

    /**
     * Market sorter function
     *
     * @param   {Object} a
     * @param   {Object} b
     *
     * @returns {Integer}
     *
     * @private
     */
    sortMarkets: function sortMarkets(a, b) {

        var mktA = window.WH.sportsbook.model.markets[a],
            mktB = window.WH.sportsbook.model.markets[b];

        var pos = markets.sortByPosition(mktA, mktB);
        if (pos === 0) {
            pos = markets.samePositionSorter(mktA, mktB);
        }

        return pos;
    },

    /**
     * Sort two markets by position.
     *
     * @param {Object} a
     * @param {Object} b
     *
     * @returns {Integer}
     *
     * @private
     */
    sortByPosition: function sortByPosition(a, b) {

        if (a && b &&
            a.hasOwnProperty('position') && b.hasOwnProperty('position') &&
            a.position !== b.position) {
            return markets.getSortedPosition(a, b);
        } else {
            return 0;
        }
    },

    /**
     * Decides if we should sort by name or by order
     *
     * @param {Object} a
     * @param {Object} b
     *
     * @returns {Integer}
     *
     * @private
     */
    samePositionSorter: function samePositionSorter(a, b) {

        if (a && b &&
            a.hasOwnProperty('position') && a.position !== 9999) {
            return markets.sortByName(a, b);
        } else {
            return markets.sortByOrder(a, b);
        }
    },

    /**
     * Sort two markets by name. Gives a non-zero result only i.e. does
     * not return 0 for equal cases.
     *
     * @param {Object} a
     * @param {Object} b
     *
     * @returns {Integer}
     *
     * @private
     */
    sortByName: function sortByName(a, b) {

        if (a && b && a.name > b.name) {
            return 1;
        } else {
            return -1;
        }
    },

    /**
     * Sort two markets by order. Gives a non-zero result only i.e. does
     * not return 0 for equal cases.
     *
     * @param {Object} a
     * @param {Object} b
     *
     * @returns {Integer}
     *
     * @private
     */
    getSortedOrder: function getSortedOrder(a, b) {

        if (a && b && a.order > b.order) {
            return 1;
        } else {
            return -1;
        }
    },

    /**
     * Sort two markets by order.
     *
     * @param {Object} a
     * @param {Object} b
     *
     * @returns {Integer}
     *
     * @private
     */
    sortByOrder: function sortByOrder(a, b) {

        if (a && b && a.order !== b.order) {
            return markets.getSortedOrder(a, b);
        } else {
            return markets.sortByName(a, b);
        }
    },

    /**
     * Sort two markets by position. Gives a non-zero result only i.e. does
     * not return 0 for equal cases.
     *
     * @param {Object} a
     * @param {Object} b
     *
     * @returns {Integer}
     *
     * @private
     */
    getSortedPosition: function getSortedPosition(a, b) {

        if (a.position > b.position) {
            return 1;
        } else {
            return -1;
        }
    },

    /**
     * Given a new market calculates each position it should be loaded in the page
     * based on all the previous processment already performed on the backend
     *
     * @param  {Object} newMarket         market for window.WHich we need to calculate the sorting position
     * @param  {Object} collectionMarkets markets groups contained in the current selected collection
     * @param  {Array} indexedMarkets    ordered index of the current position of each market
     *
     * @return {number}                   new market sorting position
     */
    sort: function sort(newMarket, collectionMarkets, indexedMarkets) {

        // set position for the new market
        markets.setPosition(collectionMarkets, newMarket);

        // append new market id to ordered index of all markets
        indexedMarkets.push(newMarket.id);

        indexedMarkets.sort(markets.sortMarkets);

        // sort it
        return indexedMarkets.indexOf(newMarket.id);
    },

    /**
     * Sets the selection groups
     *
     * @param  {Object} market for window.WHich we should set the selections groups
     *
     * @return {void}
     *
     * @private
     */
    setSelectionGroups: function setSelectionGroups(market) {

        // for compatibility with the current sortInColumns method defined in SelectionViewAdapter
        market.marketSort = market.sort;


        market.selectionGroups = SelectionViewAdapter.sortInColumns(market.selections, market);
    },
};

var selectionsObj = {

    /**
     * Filters displayed selection
     *
     * @param  {Object} selection Selection
     *
     * @returns {Boolean} True if the selection is displayed
     *
     * @private
     */
    filterByDisplayed: function filterByDisplayed(selection) {

        return (
            selection &&
            selection.hasOwnProperty('displayed') &&
            selection.displayed.toString() === 'true'
        );
    },

    /**
     * Filters an array of selections by removing all non displayed selections
     *
     * @param  {Array} selections to be filtered out
     *
     * @return {Array}            filtered array of selections
     *
     * @private
     */
    filter: function filter(selections) {

        return _.filter(selections, selectionsObj.filterByDisplayed);
    },
};

/**
 * api var for methods included in this module's return statement
 *
 * @type {Object}
 */
var api = {

    /**
     * Processes an array of markets
     *
     * @param  {Array} markets array of markets to be processed
     *
     * @return {Array}         array of processed markets
     *
     * @public
     */
    processMarkets: function processMarkets(markets) {

        _.each(markets, api.processMarket);
    },

    /**
     * Processes a given market
     *
     * @param  {Object} market market to be processed
     *
     * @return {Object}        processed market
     *
     * @public
     */
    processMarket: function processMarket(market) {

        if (!markets.filterByCollection(market, window.WH.sportsbook.collections.selected.markets) &&
            !markets.unassignedMarket(market)) {
            return false;
        }

        var processedMarket = markets.clone(market);

        // selection groups - unclear if we actually need it here
        markets.setSelectionGroups(processedMarket);

        // sort the markets according to the selected colletion data
        processedMarket.lazySortOrder = markets.sort(
            processedMarket,
            window.WH.sportsbook.collections.selected.markets,
            window.WH.sportsbook.indexedMarkets
        );

        return processedMarket;
    },

    /**
     * Processes an array of selections
     *
     * @param  {Array} selections  array of selections to be processed
     * @param  {String} marketId  market ID
     *
     * @returns {Array}            array of processed selections
     *
     * @public
     */
    processSelections: function processSelections(selections, marketId) {

        if (typeof _.first(selections) !== 'object') {
            selections = _.where(window.WH.sportsbook.model.selections, { marketId: marketId });
        }

        var filteredSelections = selectionsObj.filter(selections);


        // sort selections
        return _.map(
            filteredSelections,
            api.processSelection
        );
    },

    /**
     * Processes a given selection
     *
     * @param  {Object} selection selection object to process
     *
     * @return {Object}           new processed selection
     *
     * @public
     */
    processSelection: function processSelection(selection) {

        /**
         * Returns market ID if available
         *
         * @return {String} Market ID
         */
        var getMarketId = function () {

            if (selection.hasOwnProperty('ancestors') && selection.ancestors.length > 1) {
                return selection.ancestors[1];
            }
            return selection.marketId;
        };

        /**
         * Returns event ID if available
         *
         * @return {String} Market ID
         */
        var getEventId = function () {

            if (selection.hasOwnProperty('ancestors') && selection.ancestors.length > 0) {
                return selection.ancestors[0];
            }
            return selection.eventId;
        };

        return _.extend({}, selection, {
            name: handleName(selection),
            marketId: getMarketId(),
            eventId: getEventId(),
            currentPriceDen: parseInt(selection.currentPriceDen, 10),
            currentPriceNum: parseInt(selection.currentPriceNum, 10),
        });
    },
};

export default api;
        